import { Site } from "api/types/site";
import { t } from "i18next";
import { CONST } from "models/constants";
import LabeledField from "providers/labeledField";
import { useState } from "react";
import { MdEditDocument, MdPerson } from "react-icons/md";
import GenericCard from "widgets/cards/generic";
import SiteInfoDialog from "widgets/dialogs/siteInfo";
import CircleButton from "widgets/controls/buttons/circle-button";
import QrCodeButton from "widgets/controls/buttons/qrcode";
import { Recipients } from "api/types/device";
import RecipientsDialog from "widgets/dialogs/recipients";

interface GeneralProps {
  site?: Site;
  onUpdate?: () => void;
}

const General: React.FC<GeneralProps> = ({ site, onUpdate }) => {
  const [showSiteInfo, setShowSiteInfo] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<Recipients[]>(
    []
  );

  const handleClose = (refresh: boolean) => {
    setShowSiteInfo(false);
    setSelectedRecipients([]);
    setShowRecipients(false);
    if (refresh) {
      onUpdate();
    }
  };

  const handleOpenRecipients = () => {
    setSelectedRecipients(site.recipients);
    setShowRecipients(true);
  };

  return (
    <>
      {showSiteInfo && (
        <SiteInfoDialog
          site={site}
          isOpen={showSiteInfo}
          onClose={() => {
            handleClose(false);
          }}
          onSuccess={() => {
            handleClose(true);
          }}
        />
      )}
      {showRecipients && (
        <RecipientsDialog
          context="site"
          noLevels={site?.escalationLevels}
          id={site.id}
          values={selectedRecipients}
          isOpen={showRecipients}
          onClose={() => handleClose(false)}
          onSuccess={() => handleClose(true)}
        />
      )}
      <GenericCard
        title={site?.name}
        bgColor="500"
        extraTitle="text-white heading-md h-fit"
        topRight={
          <div className="-mt-2 flex">
            <CircleButton
              icon={MdPerson}
              size="md"
              onClick={handleOpenRecipients}
              variant="ghost"
              color="white"
              permissionRole={CONST.ROLE_MANAGER}
              tooltip={t("tooltip.recipients")}
              tooltipPlacement="bottom"
            />
            <CircleButton
              icon={MdEditDocument}
              size="md"
              color="white"
              onClick={() => {
                setShowSiteInfo(true);
              }}
              tooltip={t("tooltip.edit.site")}
              tooltipPlacement="bottom"
              variant="ghost"
              permissionRole={CONST.ROLE_MANAGER}
            />
            <QrCodeButton data={site?.id} type="site" />
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("form.input.site.name")}
            id={"site-name"}
          >
            <div className="text-txt-lg text-white">
              {site?.name || t("form.input.site.name")}
            </div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("form.input.site.reference")}
            id={"site-reference"}
          >
            <div className="text-txt-lg text-white">
              {site?.reference || t("form.input.site.reference")}
            </div>
          </LabeledField>
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("form.input.site.area")}
            id={"site-area"}
          >
            <div className="text-txt-lg text-white">
              {site?.siteArea || t("form.input.site.area")}
            </div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("form.input.site.id")}
            id={"site-id"}
          >
            <div className="text-txt-lg text-white">
              {site?.siteId || t("form.input.site.id")}
            </div>
          </LabeledField>
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("form.input.site.report.email")}
            id={"site-report-email"}
          >
            <div className="text-txt-lg text-white">
              {site?.reportEmail || t("form.input.site.report.email")}
            </div>
          </LabeledField>
        </div>
      </GenericCard>
    </>
  );
};

export default General;
